import { z } from 'zod';
import { LEAD_PUBLIC_CODE_REGEX } from './leads.js';
import { LeadStageCodeSchema } from './stages.js';
import { LEAD_TASK_TITLE_MAX, LeadTaskKindSchema } from './tasks.js';

/**
 * @aiPurpose IANA timezone string used for My Day bucket boundaries
 *   (#1082). Wire-level validation is deliberately loose — the backend
 *   re-validates with `Intl.DateTimeFormat` to reject unknown zones, so
 *   the only client-side check is a length cap and a charset gate that
 *   keeps obviously malicious payloads out of the structured response.
 */
export const MY_DAY_TIMEZONE_MAX = 64;
export const MyDayTimezoneSchema = z
  .string()
  .min(1)
  .max(MY_DAY_TIMEZONE_MAX)
  .regex(/^[A-Za-z][A-Za-z0-9_+\-/]+$/);

export const MY_DAY_DEFAULT_TIMEZONE = 'Europe/Moscow';

/**
 * @aiPurpose closed catalog of My Day buckets. `blocked` is reserved
 *   for stage rows that surface a process-error signal — the current
 *   `LeadTask` schema has no such column, so the backend ALWAYS returns
 *   an empty list for that bucket. Defined in the wire contract now so
 *   sibling #1083 can render the placeholder without a future wire
 *   change once a backing column exists.
 */
export const MyDayBucketKindSchema = z.enum(['overdue', 'today', 'upcoming', 'blocked']);
export type MyDayBucketKind = z.infer<typeof MyDayBucketKindSchema>;

/**
 * @aiPurpose compact PII-safe row for the My Day queue. Carries enough
 *   to render a task card (title, due, stage origin, navigation code)
 *   and to deep-link into the lead workspace; deliberately omits
 *   `Lead.name` / `Lead.phone` so the queue can be rendered without
 *   the manager-side PII grant.
 * @aiPii a dated MANUAL self-task `title` is user-entered free text that
 *   MAY carry PII (#1803); it is surfaced only to an actor holding
 *   `tasks.read` + lead row-visibility (already entitled to that lead's
 *   work) and is never written to logs or the audit feed. The lead PII
 *   stays behind the lead detail route.
 * @aiInvariant `dueAt` is non-nullable here even though the column is
 *   `DateTime?`: My Day excludes NULL anchors per the schema comment in
 *   `apps/backend/prisma/schema/lead-tasks.prisma`.
 */
export const MyDayItemSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    leadPublicCode: z.string().regex(LEAD_PUBLIC_CODE_REGEX),
    kind: LeadTaskKindSchema,
    stageEntryCode: LeadStageCodeSchema.nullable(),
    title: z.string().min(1).max(LEAD_TASK_TITLE_MAX),
    dueAt: z.string().datetime(),
    assigneeId: z.string().uuid().nullable(),
  })
  .strict();
export type MyDayItem = z.infer<typeof MyDayItemSchema>;

export const MyDayBucketsSchema = z
  .object({
    overdue: z.array(MyDayItemSchema),
    today: z.array(MyDayItemSchema),
    upcoming: z.array(MyDayItemSchema),
    blocked: z.array(MyDayItemSchema),
  })
  .strict();
export type MyDayBuckets = z.infer<typeof MyDayBucketsSchema>;

export const MyDayCountsSchema = z
  .object({
    overdue: z.number().int().nonnegative(),
    today: z.number().int().nonnegative(),
    upcoming: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
  })
  .strict();
export type MyDayCounts = z.infer<typeof MyDayCountsSchema>;

/**
 * @aiPurpose response envelope for `GET /api/lead-tasks/my-day`. Echoes
 *   the resolved `now` and `timezone` so the client can render relative
 *   labels against the same clock the server used for bucket math.
 *   Counts mirror `buckets.*.length` and exist purely as a navigation
 *   aid (badge totals); they are derivable but cheap to ship inline.
 */
export const MyDayResponseSchema = z
  .object({
    now: z.string().datetime(),
    timezone: MyDayTimezoneSchema,
    buckets: MyDayBucketsSchema,
    counts: MyDayCountsSchema,
  })
  .strict();
export type MyDayResponse = z.infer<typeof MyDayResponseSchema>;

export const MyDayQuerySchema = z
  .object({
    tz: MyDayTimezoneSchema.default(MY_DAY_DEFAULT_TIMEZONE),
  })
  .strict();
export type MyDayQuery = z.infer<typeof MyDayQuerySchema>;

/**
 * @aiPurpose lean PII-safe row for the duplicate-attention queue
 *   (`GET /api/lead-tasks/attention`, #1260). Surfaces MANUAL attention
 *   tasks raised by call-center duplicate intake AND `CALLBACK_URGENT`
 *   urgent callbacks (#1382) INDEPENDENT of lead lifecycle
 *   (archived/terminal leads still surface), so the responsible internal
 *   user can discover the work. `CALLBACK_URGENT` tasks are intentionally
 *   dual-surfaced — also in My Day — for urgency, per #1382.
 * @aiPii an open MANUAL `title` is user-entered free text that MAY carry
 *   PII (#1803); it is surfaced only to an actor holding `tasks.read` +
 *   lead row-visibility and is never logged or audited. Carries the lead
 *   public code + `title` + `kind` only, never the intake
 *   `phone`/`name`/`comment`/`audioUrl`. The lead PII stays behind the
 *   visibility-gated lead detail route.
 */
export const LeadAttentionTaskSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    leadPublicCode: z.string().regex(LEAD_PUBLIC_CODE_REGEX),
    kind: LeadTaskKindSchema,
    title: z.string().min(1).max(LEAD_TASK_TITLE_MAX),
    assigneeId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type LeadAttentionTask = z.infer<typeof LeadAttentionTaskSchema>;

export const GetLeadAttentionResponseSchema = z
  .object({
    items: z.array(LeadAttentionTaskSchema),
  })
  .strict();
export type GetLeadAttentionResponse = z.infer<typeof GetLeadAttentionResponseSchema>;
